import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import style from './VideoSection.module.scss';
import { Fade } from 'react-awesome-reveal';
import { scroller } from 'react-scroll';
import { Button } from '../Button';
import { Typography } from '../Typography';

// Skip autoplaying the hero video on mobile viewports or when the user has
// opted into reduced data / reduced motion - these make the ~1MB video an
// expensive, unwanted download. Deliberately NOT gating on
// connection.effectiveType: Chrome's Network Information API estimates it
// from recent RTT/bandwidth samples and frequently misreports fast
// connections as "3g" (confirmed - it silently disabled autoplay for a
// perfectly good connection), so it's too unreliable to use here.
const shouldAutoplayVideo = () => {
  if (typeof window === 'undefined') return false;

  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;

  if (connection?.saveData) return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  if (window.matchMedia('(max-width: 767px)').matches) return false;

  return true;
};

const VideoSection = ({ data }: any) => {
  const heroComponents = data?.components?.find((comp: any) => comp.name === 'hero_section');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [autoplay, setAutoplay] = useState(false);

  useEffect(() => {
    setAutoplay(shouldAutoplayVideo());
  }, []);

  useEffect(() => {
    if (autoplay) {
      videoRef.current?.play().catch(() => {
        // Autoplay can still be blocked by the browser; the poster image
        // stays visible in that case, so no fallback is needed here.
      });
    }
  }, [autoplay]);

  const handleScroll = () => {
    scroller.scrollTo('product', {
      duration: 200,
      delay: 0,
      smooth: 'easeInOutCubic',
      offset: -90,
    });
  };

  return (
    <div className={style.pageWrapper}>
      <div className={style.heroSection}>
        {/* Video Background */}
        <div className={style.videoBackground}>
          {autoplay ? (
            <video
              ref={videoRef}
              muted
              loop
              playsInline
              preload="none"
              poster="/hero-poster.jpg"
              className={style.video}
            >
              <source src="/bannervideo.webm" type="video/webm" />
              <source src="/bannervideo.mp4" type="video/mp4" />
            </video>
          ) : (
            <Image
              src="/hero-poster.jpg"
              alt=""
              aria-hidden="true"
              layout="fill"
              objectFit="cover"
              className={style.video}
              priority
            />
          )}
          <div className={style.overlay}></div>
        </div>

        <div className={style.content}>
          <Fade triggerOnce duration={800} delay={200}>
            <Typography
              variant="display-2xl"
              as="h1"
              style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}
            >
              Video Plugin Development for Adobe Premiere & DaVinci Resolve
            </Typography>
            <div className={style.heroImageWrapper}>
              <img
                src="/home-hero-lg.svg"
                alt="EPEN Ltd. Video Plugin Development Solutions for Adobe Premiere and DaVinci Resolve"
                className={style.homeHeroImageLg}
              />
              <img
                src="/home-hero.svg"
                alt="EPEN Ltd. Video Plugin Development Solutions for Adobe Premiere and DaVinci Resolve"
                className={style.homeHeroImage}
              />
            </div>
          </Fade>

          <Fade triggerOnce duration={800} delay={400}>
            <div className={style.ctaButton}>
              <Button
                size="lg"
                onClick={handleScroll}
                className={style.productsButton}
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.83398 10.8333L10.0007 15L14.1673 10.8333M5.83398 5L10.0007 9.16667L14.1673 5"
                      stroke="#D6BBFB"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                iconPosition="both"
              >
                Products
              </Button>
            </div>
          </Fade>
        </div>
      </div>
    </div>
  );
};

export default VideoSection;
